// src/app/app.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { StartupService } from './services/startup.service';
import { SharedService } from './shared/services/shared.service';
import { AccountingService } from './accounting/services/accounting.service';
import { DistributorService } from './shared/services/distributor.service';
import { ExtensionService } from './shared/services/extension.service';
import { WebsocketService } from './shared/services/websocket.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { ChatbotService } from './chatbot/chatbot.service';
import { FaqService } from './help/faq/faq.service';
import { InactivityService } from './shared/services/inactivity.service';
import { NetworkSyncService } from './shared/services/network-sync.service';
import { DeltaSyncService } from './shared/services/delta-sync.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ChangePasswordComponent } from './shared/modals/change-password/change-password.component';
import { SyncQueueModalComponent } from './shared/modals/sync-queue/sync-queue-modal.component';
import { SessionTimeoutModalComponent } from './shared/modals/session-timeout/session-timeout-modal.component';
import { CalendarService } from './calendar/services/calendar.service';
import { getFeatureVisibility } from './shared/utils/misc.utils';
import { AgGridService } from './shared/services/ag-grid.service';
import { AtfFflSyncService } from './shared/services/atf-ffl-sync.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'test-app';
  initial_page: string = 'reserved-firearms';
  message: string = '';
  receivedMessages: string[] = [];
  currentRoute: string = '';
  private readonly publicRoutePrefixes = [
    '/landing-page',
    '/landing-page-about',
    '/landing-page-contact',
    '/landing-page-competitor-comparison',
    '/landing-page-sign-up',
    '/login',
  ];
  private readonly noSidenavRoutes = [
    ...this.publicRoutePrefixes,
    '/kiosk-mode',
    '/buyer-form-4473',
  ];
  private subscription: Subscription = new Subscription();
  private servicesInitialized = false;
  private inventoryListenerAttached = false;
  private sessionTimeoutModalRef: BsModalRef | null = null;

  get isChatbotVisible(): boolean {
    if (
      this.sharedService.kiosk_mode ||
      this.currentRoute.startsWith('/buyer-form-4473') ||
      this.isPublicRoute(this.currentRoute)
    ) {
      return false;
    }
    
    const hideChatbot = localStorage.getItem('hide_chatbot');
    if (hideChatbot === 'true') {
      return false;
    }

    return getFeatureVisibility('global', 'chatbot') !== false;
  }

  get isSidenavVisible(): boolean {
    return !this.sharedService.kiosk_mode && !this.isNoSidenavRoute(this.currentRoute);
  }

  private isPublicDomain(hostname: string): boolean {
    return hostname.includes('fflxcel.com') || hostname === 'localhost';
  }

  private isPublicRoute(pathname: string): boolean {
    return this.publicRoutePrefixes.some(prefix => pathname.startsWith(prefix));
  }

  private isPublicBootstrapRoute(pathname: string): boolean {
    return this.isPublicRoute(pathname) || (this.isPublicDomain(window.location.hostname) && (pathname === '/' || pathname === ''));
  }

  private isNoSidenavRoute(pathname: string): boolean {
    return this.noSidenavRoutes.some(prefix => pathname.startsWith(prefix)) || 
           (this.isPublicDomain(window.location.hostname) && (pathname === '/' || pathname === ''));
  }
  //   title = 'test-app';
  //   initial_page: string = 'reserved-firearms'
  //   // 'buyer-form-4473'
  //   // 'seller-form-4473'
  //   // 'login'
  //   // 'home'
  //   // 'acquisition'
  //   // 'disposition'
  //   // 'disposition-selection'
  //   // 'bound-book'
  //   // 'acquisition-bulk'
  //   // 'account-dashboard'
  //   // 'account-management'
  //   // 'accounting-expense'
  //   // 'accounting-expense-table'
  //   // 'accounting-products-services'
  //   // 'accounting-sales-table'
  //   // 'accounting-sales-receipt'
  //   // 'accounting-contacts-table'
  //   // 'ffl-management'
  //   // 'firearm-management'
  //   // 'auto-purchase-history'
  //   // 'inventory-request-lists'
  //   // 'vendor-dashboard'
  //   // 'reserved-4473'
  //   // 'reserved-firearms'
  //   // 'drivers-license-selection'

  constructor(
    public startupService: StartupService,
    public sharedService: SharedService,
    public accountingService: AccountingService,
    public distributorService: DistributorService,
   // private extensionService: ExtensionService,
    private websocketService: WebsocketService,
    private chatbotService: ChatbotService,
    private faqService: FaqService,
    private calendarService: CalendarService,
    private router: Router,
    private inactivityService: InactivityService,
    private modalService: BsModalService,
    public networkSyncService: NetworkSyncService,
    private deltaSyncService: DeltaSyncService,
    private agGridService: AgGridService,
    private atfFflSyncService: AtfFflSyncService,
  ) {
    console.log('[AppComponent] Constructor: Injecting ExtensionService');
    this.currentRoute = window.location.pathname || this.router.url;
    
    // Track route changes for dynamic layout classes.
    this.subscription.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: any) => {
        this.currentRoute = event.urlAfterRedirects;

        if (this.currentRoute.startsWith('/buyer-form-4473')) {
          this.chatbotService.closeChatbot();
        }

        // Domain-based smart routing
        const hostname = window.location.hostname;
        // Only redirect if we are at the root
        if (this.currentRoute === '/' || this.currentRoute === '') {
          if (hostname.includes('fflxcel.net')) {
            this.router.navigate(['/login']);
          } else if (this.isPublicDomain(hostname)) {
            this.router.navigate(['/landing-page']);
          }
        }

      })
    );

    // Show a "still there?" countdown modal for the last 30s before an inactivity logout.
    this.subscription.add(
      this.inactivityService.showWarning$.subscribe((show) => {
        if (show) {
          this.sessionTimeoutModalRef = this.modalService.show(SessionTimeoutModalComponent, {
            class: 'modal-dialog-centered',
            backdrop: 'static',
            ignoreBackdropClick: true,
            keyboard: false,
          });
        } else {
          this.sessionTimeoutModalRef?.hide();
          this.sessionTimeoutModalRef = null;
        }
      })
    );

    // Resync anything that changed while the socket was disconnected (backend
    // restart, network blip, laptop sleep) — live-push events during that
    // window have no other backfill mechanism.
    this.subscription.add(
      this.websocketService.reconnected$.subscribe(() => {
        this.resyncAfterReconnect();
      })
    );
  }

  private async resyncAfterReconnect(): Promise<void> {
    const companyId = this.sharedService.company_info?.company_id;
    if (!companyId) return;
    try {
      let useDeltaSync = false;
      if (this.deltaSyncService.enabled) {
        await this.deltaSyncService.sync(companyId);
        useDeltaSync = true;
      }
      await Promise.all([
        this.accountingService.getVendors(useDeltaSync),
        this.accountingService.getCustomers(useDeltaSync),
        this.accountingService.getSalesReceipts(useDeltaSync),
        this.accountingService.getProductsServices(useDeltaSync),
        this.sharedService.getFirearmList(useDeltaSync),
        this.sharedService.getFFLList(useDeltaSync),
      ]);
    } catch (error) {
      console.error('[AppComponent] Resync after reconnect failed:', error);
    }
  }

  async ngOnInit() {
    // console.log(
    //   '[AppComponent] ngOnInit: Starting initialization',
    //   'Time:',
    //   new Date().toISOString()
    // );
    // // Send checkExtension early
    // console.log('[AppComponent] Sending checkExtension request');
    // window.postMessage({ checkExtension: true }, window.location.origin);

    // // Retry checkExtension
    // const retryInterval = setInterval(() => {
    //   console.log('[AppComponent] Sending retry checkExtension request');
    //   window.postMessage({ checkExtension: true }, window.location.origin);
    // }, 1000);
    // setTimeout(() => clearInterval(retryInterval), 15000);

    const initialPath = window.location.pathname || this.router.url;
    const isPublicRoute = this.isPublicBootstrapRoute(initialPath);

    // Never run authenticated bootstrap on public landing or login routes.
    if (!isPublicRoute) {
      await this.tryAutoLogin();

      if (this.sharedService.logged_in_user?.user_id) {
        await this.initializePostLoginData();
      }
    }

    // console.log(
    //   '[AppComponent] ngOnInit: ExtensionService initialized',
    //   'Time:',
    //   new Date().toISOString()
    // );
    
    // Listen for manual logins to check for forced password change
    this.subscription.add(
      this.startupService.user_successfully_logged_in$.subscribe(() => {
        // Force re-initialization of all services. This is crucial if the user was
        // logged out due to inactivity without a page reload; we need fresh data.
        this.servicesInitialized = false;
        this.initializePostLoginData();
        this.checkForcedPasswordChange();
      })
    );

    // Notify the user when a queued offline change is rejected by the server (409 LWW conflict)
    this.subscription.add(
      this.networkSyncService.conflictErrors$.subscribe((conflict) => {
        this.sharedService.raiseWarningToast(
          conflict.message,
          'Sync Conflict',
        );
      })
    );

    // If the session expires while offline changes are queued, halt sync and prompt re-login.
    // The queue is preserved — changes will sync after the user logs back in.
    this.subscription.add(
      this.networkSyncService.authError$.subscribe(() => {
        while (this.modalService.getModalsCount() > 0) { this.modalService.hide(); }
        this.sharedService.raiseWarningToast(
          'Your session has expired. Please log in again — your pending changes are safe and will sync after you sign back in.',
          'Session Expired',
        );
        this.router.navigate(['/login']);
      })
    );
  }

  openSyncQueueModal(): void {
    this.modalService.show(SyncQueueModalComponent, {
      class: 'modal-xl modal-dialog-centered',
      animated: true,
    });
  }

  private async initializePostLoginData(): Promise<void> {
    if (this.servicesInitialized) {
      return;
    }

    this.servicesInitialized = true;
    const warmupErrorCount = await this.initializeServices();
    if (warmupErrorCount > 0) {
      // Allow a future retry (manual re-login/refresh) if warmup was partial.
      this.servicesInitialized = false;
    }
    this.websocketService.ensureConnected();
    this.websocketService.loadAlertsForCurrentUser();

    if (!this.inventoryListenerAttached) {
      this.inventoryListenerAttached = true;
      this.listenForInventoryAlerts();
    }

    // Not awaited: the first sync on a device downloads the full ATF FFL
    // list (~75k records) to IndexedDB for offline FFL Lookup access. That
    // shouldn't hold up the login spinner — it runs quietly in the
    // background and every sync after the first is a small incremental one.
    this.atfFflSyncService.sync().catch((error) => {
      console.error('[AppComponent] ATF FFL list sync failed:', error);
    });
  }

  private async tryAutoLogin(): Promise<void> {
    // Only attempt if we have a cached user hint (avoids API call when never logged in).
    // The actual authentication is validated server-side via the httpOnly cookie.
    if (!localStorage.getItem('cached_user')) return;
    try {
      const account = await firstValueFrom(this.startupService.verifyToken());
      const user = {
        user_id: account.user_id || account.id,
        email: account.email,
        username: account.username,
        role: account.role,
        company_id: account.company_id,
        name_first: account.name_first,
        name_last: account.name_last,
        must_change_password: account.must_change_password || false,
      };
      this.sharedService.logged_in_user = user as any;
      // Cache minimal non-PII fields for offline fallback
      localStorage.setItem('cached_user', JSON.stringify({
        user_id: user.user_id,
        role: user.role,
        company_id: (user as any).company_id,
        username: user.username,
        must_change_password: (account as any).must_change_password || false,
      }));
      this.inactivityService.startWatching();
      this.checkForcedPasswordChange();
    } catch (err: any) {
      const isNetworkError = !navigator.onLine || err?.status === 0;
      if (isNetworkError) {
        // Offline — restore from cached session rather than forcing a re-login
        const cached = localStorage.getItem('cached_user');
        if (cached) {
          this.sharedService.logged_in_user = JSON.parse(cached);
          this.inactivityService.startWatching();
        } else {
          console.warn('[AppComponent] Offline and no cached user — cannot auto-login.');
        }
      } else {
        // Server explicitly rejected the session (401/403) — clear local cache
        localStorage.removeItem('remember_me_email');
        localStorage.removeItem('cached_user');
      }
    }
  }

  private checkForcedPasswordChange(): void {
    const user = this.sharedService.logged_in_user;
    if (user && (user as any).must_change_password && (user as any).role !== 'Developer') {
      this.modalService.show(ChangePasswordComponent, {
        initialState: {
          userId: (user as any).user_id || (user as any).id,
          isForced: true,
        },
        ignoreBackdropClick: true,
        keyboard: false,
        class: 'modal-dialog-centered',
      });
    }
  }

  private async initializeServices(): Promise<number> {
    console.log('[AppComponent] initializeServices: Starting');
    const warmupFailures: string[] = [];
    const runWarmupStep = async (
      label: string,
      action: () => Promise<unknown>,
    ): Promise<void> => {
      try {
        await action();
      } catch (error) {
        warmupFailures.push(label);
        console.error(`[AppComponent] Warmup failed for ${label}:`, error);
      }
    };

    try {
      this.sharedService.openStatusSpinner('Fetching Company Records');
      await runWarmupStep('company profile', async () => {
        await this.sharedService.getCompanyInformation();
      });

      const companyId = this.sharedService.company_info.company_id || '';
      if (!companyId) {
        warmupFailures.push('company id');
      } else {
        let useDeltaSync = false;
        if (this.deltaSyncService.enabled) {
          try {
            await this.deltaSyncService.sync(companyId);
            useDeltaSync = true;
          } catch (error) {
            console.error('[AppComponent] Delta sync failed; using full refresh:', error);
          }
        }
        await Promise.all([
          runWarmupStep('ffl document', async () => {
            const result = await this.sharedService.getFFLDocument(companyId);
            this.sharedService.company_info.ffl_document = result.document;
            this.sharedService.company_info.ffl_document_meta = {
              file_name: result.file_name,
              file_size_kb: result.file_size_kb,
              uploaded_at: result.updatedAt,
            };
          }),
          runWarmupStep('sot document', async () => {
            const result = await this.sharedService.getSOTDocument(companyId);
            this.sharedService.company_info.sot_document = result.document;
            this.sharedService.company_info.sot_document_meta = {
              file_name: result.file_name,
              file_size_kb: result.file_size_kb,
              uploaded_at: result.updatedAt,
            };
          }),
          runWarmupStep('vendors', async () => {
            await this.accountingService.getVendors(useDeltaSync);
          }),
          runWarmupStep('customers', async () => {
            await this.accountingService.getCustomers(useDeltaSync);
          }),
          runWarmupStep('sales receipts', async () => {
            await this.accountingService.getSalesReceipts(useDeltaSync);
          }),
          runWarmupStep('products/services', async () => {
            await this.accountingService.getProductsServices(useDeltaSync);
          }),
          runWarmupStep('firearms', async () => {
            await this.sharedService.getFirearmList(useDeltaSync);
          }),
          runWarmupStep('ffl list', async () => {
            await this.sharedService.getFFLList(useDeltaSync);
          }),
          runWarmupStep('users', async () => {
            await this.sharedService.getAllUsers();
          }),
          runWarmupStep('bound books', async () => {
            await this.sharedService.getBoundBookEntries(useDeltaSync);
          }),
          runWarmupStep('reserved firearms', async () => {
            await this.sharedService.getReservedFirearmList(useDeltaSync);
          }),
          runWarmupStep('reserved 4473', async () => {
            await this.sharedService.getReserved4473List(useDeltaSync);
          }),
          runWarmupStep('form 4473', async () => {
            await this.sharedService.getForm4473ByCompanyId(companyId, useDeltaSync);
          }),
          runWarmupStep('calendar events', async () => {
            await this.calendarService.getEvents(useDeltaSync);
          }),
          runWarmupStep('driver licenses', async () => {
            await this.sharedService.getRecentDriverLicenses();
          }),
          runWarmupStep('distributor configs', async () => {
            await this.distributorService.getAllDistributorConfigs();
          }),
          runWarmupStep('table settings', async () => {
            await this.agGridService.loadAllSavedTableSettings();
          }),
        ]);
      }

      // Keep derived in-memory lists in sync after vendor cache warmup.
      this.accountingService.getVendorList();
      this.getFaqs();
    } catch (e) {
      warmupFailures.push('bootstrap');
      console.error('[AppComponent] Error in initializeServices:', e);
    } finally {
      this.sharedService.closeStatusSpinner();
    }

    if (warmupFailures.length > 0) {
      this.sharedService.raiseWarningToast(
        `Offline cache warmup partially failed (${warmupFailures.join(', ')}). Keep internet connected, then refresh once before going offline.`,
        'Offline Cache Warmup',
      );
    }

    return warmupFailures.length;
  }
  
  // sendMessage(): void {
  //   this.websocketService.sendMessage(this.message);
  //  this.message = '';
  // }

  getFaqs() {
    this.faqService.getAllFAQs().subscribe({
      next: (response) => {
       // console.log('FAQ data loaded for chatbot:', response);
        // Store FAQ data in a shared location that chatbot can access
        this.faqService.faqItems = response.data || response;
        // Or pass it to chatbot service
        this.chatbotService.setFAQData(response.data || response);
      },
      error: (error) => {
        console.error('Error loading FAQ data for chatbot:', error);
      },
    });
  }

  private listenForInventoryAlerts() {
    this.subscription.add(
      this.websocketService.getInventoryAlerts().subscribe((alert: any) => {
        console.log('📢 Inventory Alert Received:', alert);
        // Show a notification using the SharedService's success toast method
        const message = `UPC ${alert.upc} is now in stock at ${alert.vendor}!`;
        this.sharedService.raiseSuccessToast(message);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.websocketService.disconnect();
  }
}
